/**
 * The single HTTP client for the API.
 *
 * The access token is deliberately held in memory only — never localStorage — so an XSS bug
 * cannot walk away with a live session. Durability comes from the httpOnly refresh cookie the
 * server sets, which script cannot read at all.
 */

let accessToken: string | null = null
let onUnauthenticated: (() => void) | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler
}

export class ApiError extends Error {
  // Declared as fields rather than constructor parameter properties: the project builds with
  // erasableSyntaxOnly, so TypeScript-only syntax that emits code is not allowed.
  readonly status: number
  readonly detail: string | undefined
  readonly errors: Record<string, string[]> | undefined

  constructor(
    status: number,
    message: string,
    detail?: string,
    errors?: Record<string, string[]>,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
    this.errors = errors
  }

  /** The branch cap was hit — a billing limit, not a permissions problem. */
  get isLicenceLimit(): boolean {
    return this.status === 402
  }
}

interface ProblemDetails {
  title?: string
  detail?: string
  errors?: Record<string, string[]>
}

/** Tracks an in-flight refresh so ten parallel 401s trigger one refresh, not ten. */
let refreshInFlight: Promise<boolean> | null = null

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'same-origin',
      })

      if (!response.ok) return false

      const session = (await response.json()) as { accessToken: string }
      setAccessToken(session.accessToken)
      return true
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Internal: prevents a refresh loop when the retry also fails. */
  retryOnUnauthorized?: boolean
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, retryOnUnauthorized = true, headers, ...rest } = options

  const response = await fetch(path, {
    ...rest,
    credentials: 'same-origin',
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (response.status === 401 && retryOnUnauthorized) {
    // The access token is short-lived by design; a silent refresh keeps a cashier from
    // being thrown back to the login screen mid-queue.
    if (await refreshSession()) {
      return api<T>(path, { ...options, retryOnUnauthorized: false })
    }

    setAccessToken(null)
    onUnauthenticated?.()
  }

  if (!response.ok) {
    let problem: ProblemDetails = {}
    try {
      problem = (await response.json()) as ProblemDetails
    } catch {
      // A non-JSON error body (a proxy timeout page, say) still needs a usable message.
    }

    throw new ApiError(
      response.status,
      problem.title ?? problem.detail ?? `Request failed (${response.status})`,
      problem.detail,
      problem.errors,
    )
  }

  // Read as text first. A 204, or a 200 that happens to carry no body, would make
  // response.json() throw a parse error — turning a perfectly normal "nothing here" answer
  // into a failed query that the caller then has to guess the meaning of.
  //
  // Empty means `null`, never `undefined`. TanStack Query treats an undefined result as a
  // failed fetch and keeps whatever it had cached, so "there is no open shift" would silently
  // leave the previous shift on screen — the till would still look open after it was closed.
  // `null` is also the honest answer: the server said there is nothing, not that it did not say.
  const payload = await response.text()
  return (payload.length > 0 ? JSON.parse(payload) : null) as T
}

export const apiClient = {
  get: <T>(path: string) => api<T>(path),
  post: <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => api<T>(path, { method: 'DELETE' }),
}

export { refreshSession }
